import 'rsuite/dist/rsuite-no-reset.min.css'
import 'rsuite/DatePicker/styles/index.css'

/* eslint-disable boundaries/element-types */
import { zodResolver } from '@hookform/resolvers/zod'
import * as Form from '@radix-ui/react-form'
import { useParams } from '@tanstack/react-router'
import { message } from 'antd'
import { FormProvider } from 'antd/es/form/context'
import { useRuntimeVariables } from 'app-kit'
import {
  inspectionDefaultsMap,
  unknownDefaults,
} from 'components/rightDrawer/drawerStates/crudWorkflows/utils/inspectionDefaultsMap'
import { useSalesforceJobsForSite } from 'components/rightDrawer/drawerStates/crudWorkflows/utils/salesforceData'
import { StyledSelectDiv } from 'components/rightDrawer/drawerStates/crudWorkflows/utils/sharedStyles'
import {
  formValidationStyles,
  StyledFormInput,
  StyledFormSubmitButton,
} from 'components/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { useRightDrawerRolodexEntries } from 'components/rightDrawer/useRightDrawerRolodexEntries'
import { RightSideDrawerProps } from 'components/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'components/rightDrawer/utils/RightDrawerProvider'
import { SalesforceSelect } from 'components/salesforce/SalesforceSelect'
import { format } from 'date-fns'
import { convertDateToString } from 'features/assetDatabase'
import { useGeckoAuth } from 'features/auth'
import { triggerInspectionEditPipeline } from 'features/dsapi'
import { useRolodexEdit } from 'features/fde'
import { generateEntrySlug } from 'features/rolodex'
import { startCase } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { CheckPicker, CustomProvider, DatePicker, SelectPicker } from 'rsuite'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  anglePlotLookupTable,
  DropdownOption,
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoUnit,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuid4 } from 'uuid'

import {
  defaultValues,
  EditInspectionSchema,
  getConvertedAnglePlotSettings,
} from './editInspectionSchema'

export type UnitAssetModelPair = {
  unit: GeckoUnit
  assetModel: GeckoAssetModel | undefined
}
const StyledInspectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${formValidationStyles}
`
const StyledHeaderDiv = styled.div`
  padding-bottom: 15px;
`

const StyledFormRoot = styled(Form.Root)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const InputAndUnits = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.forecast.background1} !important;
  border: 1px solid ${({ theme }) => theme.forecast.border2} !important;
  border-radius: 4px;
  overflow: hidden;
`
const StyledFormInputWithUnits = styled(StyledFormInput)`
  border: none;
  flex: 1;
  min-width: 0;
  padding: 8px;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`

const FieldUnits = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: default;
  background-color: ${({ theme }) => theme.forecast.background1};
  padding: 0 12px;
  border-left: 1px solid ${({ theme }) => theme.forecast.border2};
  color: ${({ theme }) => theme.forecast.text3};
`

export const validationThicknessOptons: Array<DropdownOption> = [
  { value: 'thickness_upload', label: 'Thickness Upload' },
  { value: 'thickness_rc', label: 'Thickness RC' },
  { value: 'thickness_bootsy', label: 'Thickness Bootsy' },
]

enum DisplayComponentTypeID {
  Default = 0,
  Boiler = 1,
  TankShell = 2,
  ConeShell = 3,
  TankFloor = 4,
  Piping = 5,
  Panel = 6,
  Unknown = 7,
}

export const componentTypeOptions: Array<DropdownOption> = Object.keys(
  DisplayComponentTypeID
)
  .filter((val) => isNaN(Number(val)))
  .map((key) => {
    return {
      label: startCase(key),
      value:
        DisplayComponentTypeID[
          key as keyof typeof DisplayComponentTypeID
        ].toString(),
    } as DropdownOption
  })

export const EditGeckoInspectionProps: RightSideDrawerProps = {
  title: 'Edit Gecko Inspection',
}

export const ndtMethodOptions: Array<DropdownOption> = [
  { value: 'ut', label: 'UT' },
  { value: 'aut', label: 'AUT' },
  { value: 'paut', label: 'PAUT' },
  { value: 'emat', label: 'EMAT' },
  { value: 'mi', label: 'MI' },
  { value: 'acfm', label: 'ACFM' },
  { value: 'laser', label: 'Laser' },
  { value: 'mlo', label: 'MLO' },
  {
    value: 'pulsed-eddy-current',
    label: 'PEC',
  },
]

export const measurementOptions: Array<DropdownOption> = [
  { value: 'ft-in', label: 'ft-in' },
  { value: 'in-in', label: 'in-in' },
  { value: 'm-mm', label: 'm-mm' },
  { value: 'mm-mm', label: 'mm-mm' },
  { value: 'ft-mil', label: 'ft-mil' },
]

export const inspectionTypeOptions: Array<DropdownOption> = [
  {
    value: 'rug',
    label: 'RUG',
  },
  {
    value: 'raut',
    label: 'RAUT',
  },
  {
    value: 'phased-array',
    label: 'Phased Array',
  },
  {
    value: 'trilat',
    label: 'Trilat',
  },
  {
    value: 'lidar',
    label: 'Lidar',
  },
  {
    value: 'ie',
    label: 'IE',
  },
  {
    value: 'sasw',
    label: 'SASW',
  },
  {
    value: 'upe',
    label: 'UPE',
  },
  {
    value: 'manual',
    label: 'Manual',
  },
  {
    value: 'emat',
    label: 'EMAT',
  },
  {
    value: 'mi',
    label: 'MI',
  },
  {
    value: 'art',
    label: 'ART',
  },
  {
    value: 'pulsed-eddy-current',
    label: 'PEC',
  },
  {
    value: 'drone',
    label: 'Drone',
  },
  {
    value: 'frp',
    label: 'FRP',
  },
  {
    value: 'pt',
    label: 'Penetrant Testing',
  },
  {
    value: 'brick-tile-inspection',
    label: 'Brick and Tile Inspection',
  },
  {
    value: 'external-visual',
    label: 'External Visual',
  },
  {
    value: 'internal-visual',
    label: 'Internal Visual',
  },
  {
    value: 'profile-radiography',
    label: 'Profile Radiography',
  },
  {
    value: 'infrared-thermography',
    label: 'Infrared Thermography',
  },
  {
    value: 'spark-testing',
    label: 'Spark Testing',
  },
  {
    value: 'lift-lever-test',
    label: 'Lift Lever Test',
  },
]

export const robotTypeOptions: Array<DropdownOption> = [
  { value: 'TOKA 3', label: 'TOKA 3' },
  { value: 'TOKA 4', label: 'TOKA 4' },
  { value: 'TOKA Flex', label: 'TOKA FLEX' },
  { value: 'TOKA GZ', label: 'TOKA GZ' },
  { value: 'TOKA 5', label: 'TOKA 5' },
  { value: 'NAVIC', label: 'NAVIC' },
]

export const angleVariableOptions: Array<DropdownOption> = [
  { value: 'angle', label: 'Angle' },
  { value: 'position', label: 'Position' },
  { value: 'tube', label: 'Tube' },
]

const newInspectionEntry = {
  id: 'test',
  type: 'inspection',
  data: {},
  tags: {},
} as RolodexEntryPutJson

function anglePlotDropdownOptions(): DropdownOption[] {
  const result: DropdownOption[] = []
  for (const key of anglePlotLookupTable.keys()) {
    result.push({ value: key.toString(), label: anglePlotLookupTable.get(key) })
  }
  return result
}

export const EditGeckoInspection = () => {
  // will either be a job or an existing inspection
  const { drawerEntry, closeDrawer } = useRightDrawer()
  const { DS_API_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { siteID } = useParams({ strict: false })
  const { components, units, assetModels } = useRightDrawerRolodexEntries({
    siteID,
  })
  const { theme } = useGeckoTheme()

  // creates a mapping of unit to the most recent asset model tied to that unit
  const assetModelsByUnitID = useMemo(() => {
    const result = new Map<string, GeckoAssetModel>()
    assetModels.map((model) => {
      const unitTag = model.tags.unit
      const existingModel = result.get(unitTag)
      if (existingModel) {
        // pick the most recent asset model
        const newModelDate = new Date(model.data.effective_date)
        const existingModelDate = new Date(existingModel.data.effective_date)

        // if the exisiting model in the map has a more recent effective date, use that one
        result.set(
          unitTag,
          newModelDate >= existingModelDate ? model : existingModel
        )
      } else {
        result.set(unitTag, model)
      }
    })
    return result
  }, [assetModels])

  // map of unit id to an object that contains that unit and the most recent tagged asset model if one exists
  const unitsByID = useMemo(() => {
    const result = new Map<string, UnitAssetModelPair>()
    units.map((unit) => {
      const assetModel = assetModelsByUnitID.get(unit.id)

      result.set(unit.id, { unit: unit, assetModel: assetModel ?? undefined })
    })

    return result
  }, [assetModelsByUnitID, units])

  // create a way to lookup components to unit id
  const componentsByID = useMemo(() => {
    const result = new Map<string, GeckoComponent>()
    components.map((component) => {
      result.set(component.id, component)
    })
    return result
  }, [components])

  // create the dropdown options to select the component
  const componentOptions: Array<DropdownOption> = useMemo(() => {
    const options: DropdownOption[] = []
    components
      .toSorted((a, b) => {
        if (a.tags.unit < b.tags.unit) return -1
        if (a.tags.unit > b.tags.unit) return 1
        if (a.data.name < b.data.name) return -1
        if (a.data.name > b.data.name) return 1
        else return 0
      })
      .map((component) => {
        options.push({
          value: component.id,
          label: (
            <div>
              <span>{unitsByID.get(component.tags.unit)?.unit.data.name}</span>
              <span
                style={{
                  color: theme.forecast.text4,
                  paddingLeft: '10px',
                }}>
                {component.data.name}
              </span>
            </div>
          ),
        })
      })

    return options
  }, [components, unitsByID, theme.forecast.text4])

  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()
  const form = useForm<EditInspectionSchema>({
    mode: 'onBlur',
    resolver: zodResolver(EditInspectionSchema),
    defaultValues,
  })
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = form

  const watchAllFields = watch()

  const apsOptions = anglePlotDropdownOptions()

  const [isFulcrum, setIsFulcrum] = useState<boolean>(false)
  //tags (component is a selectable field that gets PUT as a tag)
  const [inspectionSlug, setInspectionSlug] = useState<string>('')
  const [siteSlug, setSiteSlug] = useState<string>('')
  const [orgSlug, setOrgSlug] = useState<string>('')
  const [unitSlug, setUnitSlug] = useState<string>('')
  const [jobSlug, setJobSlug] = useState<string>('')

  // creating the hash for the inspection slug
  const inspectionHash = useMemo(() => uuid4().slice(0, 6), [])

  const [currentComponentID, setCurrentComponentID] = useState(
    drawerEntry?.tags?.component ?? ''
  )

  const xBinSizeUnits = useMemo(() => {
    if (!drawerEntry) return undefined

    const assetModel = unitsByID.get(unitSlug)?.assetModel
    const componentID = currentComponentID

    if (!assetModel || !componentID) return undefined

    const componentType = assetModel.data.components.get(componentID)?.type
    if (!componentType) return undefined

    if (componentType === GeckoComponentModelType.LegacyCone) {
      return '°'
    } else if (componentType === GeckoComponentModelType.LegacyBoilerWalls) {
      return 'tubes'
    }
    return 'ft'
  }, [currentComponentID, drawerEntry, unitsByID, unitSlug])

  const newInspection = !(drawerEntry instanceof GeckoInspection)

  // parse the angle plot settings on an inspection (if inspection) to a list of ids
  const parsedAnglePlotSettings = useMemo(() => {
    if (drawerEntry && !newInspection) {
      const apsList = drawerEntry.data.angle_plot_settings
      const result: string[] = []
      apsList?.map((aps) => {
        const parsedID = aps.id.toString()
        result.push(parsedID)
      })
      return result ?? []
    }
  }, [drawerEntry, newInspection])

  const containerTitle = newInspection ? 'New' : 'Edit'
  const submitButtonText = newInspection ? 'Create' : 'Save'

  //-----------------------------------hooks-----------------------------------------------------------------------------------
  // if there is an inspection entry (aka you edit an inspection populate the slug field)
  useEffect(() => {
    if (drawerEntry) {
      if (!newInspection) {
        reset({
          date: drawerEntry.data.date,
          inspection_type: drawerEntry.data.inspection_type,
          component: drawerEntry.tags.component,
          salesforceJobId:
            drawerEntry.tags[RolodexEntryType.SALESFORCE_JOB] || '',
          ndt_method_type: drawerEntry.data.ndt_method_type,
          units: drawerEntry.data.units,
          num_points: drawerEntry.data.num_points,
          x_bin_size: drawerEntry.data.x_bin_size,
          y_bin_size: drawerEntry.data.y_bin_size,
          robot_types: drawerEntry.data.robot_types,
          plot_rotation: drawerEntry.data.plot_rotation,
          sensor_offset: drawerEntry.data.sensor_offset,
          angle_variable: drawerEntry.data.angle_variable,
          sensor_spacing: drawerEntry.data.sensor_spacing,
          angle_plot_settings: parsedAnglePlotSettings,
          validation_thickness: drawerEntry.data.validation_thickness,
          display_component_type_id: drawerEntry.data.display_component_type_id,
          vertical_runs: drawerEntry.data.vertical_runs,
          low_threshold_relative_to_nominal:
            drawerEntry.data.low_threshold_relative_to_nominal,
          high_threshold_relative_to_nominal:
            drawerEntry.data.high_threshold_relative_to_nominal,
        })

        //tags
        setInspectionSlug(drawerEntry.raw.id)
        setJobSlug(drawerEntry.tags.job)
        setUnitSlug(drawerEntry.tags.unit)
        setSiteSlug(drawerEntry.tags.site)
        setOrgSlug(drawerEntry.tags.organization)
        setIsFulcrum(drawerEntry.data.data_source === 'fulcrum')
      } else {
        // a new inspection, we got passed in a job entry
        let jobDate: string | undefined
        if (drawerEntry.data.start_date) {
          jobDate =
            drawerEntry.data.start_date instanceof Date
              ? convertDateToString(
                  new Date(drawerEntry.data.start_date),
                  'yyyy-MM-dd'
                )
              : undefined
          jobDate = jobDate?.split('T')[0].replaceAll('-', '')
        }

        setInspectionSlug(generateEntrySlug(jobDate, inspectionHash))
        setJobSlug(drawerEntry.id || '')
        setValue('component', '')
        setSiteSlug(drawerEntry.tags.site || '')
        setOrgSlug(drawerEntry.tags.organization || '')
        reset({
          robot_types: [],
          angle_plot_settings: [],
          vertical_runs: true,
          date:
            typeof drawerEntry.data.start_date === 'string' ||
            drawerEntry.data.start_date instanceof Date
              ? new Date(drawerEntry.data.start_date)
              : new Date(),
        })
      }
    }
  }, [
    drawerEntry,
    inspectionHash,
    newInspection,
    parsedAnglePlotSettings,
    reset,
    setValue,
  ])

  const onDateChange = (date: Date) => {
    // If this is a new inspection, set the slug value once a date has been picked (or changed)
    // Otherwise, ignore date changes as the slug has already been set
    if (drawerEntry && !newInspection) {
      return
    }

    const dateFieldArray = new Date(date).toISOString().split('T')

    if (!dateFieldArray || dateFieldArray.length !== 2) {
      throw new Error(
        'Unable to parse the date for generating an inspection slug. It appears the date was malformed.'
      )
    }

    const dateField = dateFieldArray[0].replaceAll('-', '')

    setInspectionSlug(generateEntrySlug(dateField, inspectionHash))
  }
  //-------------------------------------------submit handling---------------------------------------------------------

  // callback when you change components to determine which defaults
  // to fill in when you select a new component
  const onComponentChange = (componentValue: string) => {
    const unitTag = componentsByID.get(componentValue)?.tags.unit
    setUnitSlug(unitTag ?? '')
    setCurrentComponentID(componentValue)

    // get the relevant asset model for this unit
    const relevantAssetModel = unitsByID.get(unitTag ?? '')
    // search for the component that we selected
    const components = relevantAssetModel?.assetModel?.data.components
    const componentModelType = components?.get(componentValue)?.type
    const assetModelTypeFallBack = relevantAssetModel?.assetModel?.data.type
    // if we found the component model type use it other wise use asset model type
    const defaultLookup = componentModelType ?? assetModelTypeFallBack

    // if we dont have anything to lookup the default, just pass unknown -> which will use tank defaults
    const defaults = inspectionDefaultsMap.get(defaultLookup ?? 'Unknown')
    const date = getValues('date')
    if (defaults) {
      reset(defaults)
    } else {
      reset(unknownDefaults)
    }
    setValue('date', date)
    setValue('component', componentValue)
  }

  const { salesforceJobOptions } = useSalesforceJobsForSite({
    siteSlug,
    orgSlug,
    jobYear: format(new Date(watchAllFields.date || new Date()), 'yyyy%'),
    currentJobId:
      drawerEntry?.tags?.[RolodexEntryType.SALESFORCE_JOB] ?? undefined,
  })

  // on submit function to make put to rolodex
  const onSubmit: SubmitHandler<EditInspectionSchema> = (data) => {
    const inspectionRawJSON = newInspection
      ? newInspectionEntry
      : drawerEntry.raw

    inspectionRawJSON.id = inspectionSlug as string
    inspectionRawJSON.data.date = format(data.date as Date, 'yyyy-MM-dd')
    inspectionRawJSON.data.inspection_type = data.inspection_type
    inspectionRawJSON.data.ndt_method_type = data.ndt_method_type
    inspectionRawJSON.data.vertical_runs = data.vertical_runs

    const apsField = getConvertedAnglePlotSettings(
      (data.angle_plot_settings as string[]) ?? []
    )
    inspectionRawJSON.data.angle_plot_settings = apsField
    inspectionRawJSON.data.units = data.units
    inspectionRawJSON.data.num_points = data.num_points
    inspectionRawJSON.data.x_bin_size = data.x_bin_size
    inspectionRawJSON.data.y_bin_size = data.y_bin_size
    inspectionRawJSON.data.robot_types = data.robot_types
    inspectionRawJSON.data.zero_offset = null
    inspectionRawJSON.data.plot_rotation = data.plot_rotation
    inspectionRawJSON.data.sensor_offset = data.sensor_offset
    inspectionRawJSON.data.angle_variable = data.angle_variable
    inspectionRawJSON.data.sensor_spacing = data.sensor_spacing
    inspectionRawJSON.data.validation_thickness = data.validation_thickness
    inspectionRawJSON.data.display_component_type_id =
      data.display_component_type_id
    inspectionRawJSON.data.low_threshold_relative_to_nominal =
      data.low_threshold_relative_to_nominal
    inspectionRawJSON.data.high_threshold_relative_to_nominal =
      data.high_threshold_relative_to_nominal
    //tags
    inspectionRawJSON.tags.component = data.component as string
    inspectionRawJSON.tags.job = jobSlug
    inspectionRawJSON.tags.unit = unitSlug
    inspectionRawJSON.tags.site = siteSlug
    inspectionRawJSON.tags.organization = orgSlug
    if (data.salesforceJobId) {
      inspectionRawJSON.tags[RolodexEntryType.SALESFORCE_JOB] =
        data.salesforceJobId
    }

    if (data.component && data.component !== 'Unknown') {
      const action = newInspection ? 'created new' : 'modified this'
      rolodexPut([inspectionRawJSON])
        .then(() => {
          triggerInspectionEditPipeline(
            inspectionSlug,
            DS_API_URL,
            axios,
            isFulcrum
          )
            .then(() => {
              return messageApi.success(
                `Successfully ${action} inspection! Redirecting...`,
                1
              )
            })
            .catch(() => {
              messageApi.error(
                `Failed to re-trigger pipelines for inspection! Please check permissions and try again.`
              )
            })
        })
        .then(() => {
          closeDrawer()
        })
        .catch(() => {
          messageApi.error(
            `Failed to ${action} inspection! Please check permissions and try again.`
          )
        })
    } else {
      messageApi.error(`Must include a tagged component.`)
    }
  }

  return (
    <FormProvider {...form}>
      <StyledInspectionContainer>
        {contextHolder}
        <div>
          <StyledHeaderDiv>
            <h3>{containerTitle} Inspection</h3>
            <h4>Slug: {inspectionSlug}</h4>
          </StyledHeaderDiv>
          <StyledFormRoot onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name='date'
              control={control}
              render={({
                field: { onBlur, onChange, disabled, ref, value },
              }) => {
                return (
                  <>
                    <label>Date</label>
                    <CustomProvider theme='dark'>
                      <DatePicker
                        ref={ref}
                        oneTap
                        style={{
                          width: '100%',
                          height: '100%',
                        }}
                        ranges={[]}
                        onBlur={onBlur}
                        onChange={(value) => {
                          onChange(value)
                          if (value) onDateChange(value)
                        }}
                        disabled={disabled}
                        format='yyyy-MM-dd'
                        placeholder='yyyy-MM-dd'
                        value={value}
                        aria-required='true'
                        aria-invalid={!!errors.date}
                      />
                    </CustomProvider>
                    {errors.date && (
                      <span role='alert'>{errors.date.message}</span>
                    )}
                  </>
                )
              }}
            />

            <Controller
              name='salesforceJobId'
              control={control}
              render={({ field: { name, onBlur, onChange, value, ref } }) => (
                <StyledSelectDiv>
                  <label htmlFor={name}>Salesforce Inspection</label>
                  <SalesforceSelect
                    id={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    data={salesforceJobOptions}
                    showDates={true}
                  />
                  {errors.salesforceJobId && (
                    <span role='alert'>{errors.salesforceJobId.message}</span>
                  )}
                </StyledSelectDiv>
              )}
            />

            <Controller
              name='component'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => {
                return (
                  <StyledSelectDiv>
                    <label>Component</label>
                    <CustomProvider theme='dark'>
                      <SelectPicker
                        data-testid='component-combobox'
                        ref={ref}
                        onBlur={onBlur}
                        onChange={(value) => {
                          onChange(value)
                          if (value) {
                            onComponentChange(value)
                          }
                        }}
                        searchable={true}
                        value={value}
                        data={componentOptions}
                        aria-invalid={!!errors.component}
                      />
                    </CustomProvider>
                  </StyledSelectDiv>
                )
              }}></Controller>

            <Controller
              name='display_component_type_id'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => {
                return (
                  <StyledSelectDiv>
                    <label>Display Component Type</label>
                    <CustomProvider theme='dark'>
                      <SelectPicker
                        data-testid='display-component-type-combobox'
                        onBlur={onBlur}
                        onChange={onChange}
                        ref={ref}
                        searchable={false}
                        value={value}
                        data={componentTypeOptions}
                        aria-invalid={!!errors.display_component_type_id}
                      />
                    </CustomProvider>
                    {errors.display_component_type_id && (
                      <span role='alert'>
                        {errors.display_component_type_id.message}
                      </span>
                    )}
                  </StyledSelectDiv>
                )
              }}></Controller>

            <Controller
              name='units'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => {
                return (
                  <StyledSelectDiv>
                    <label>Units</label>
                    <CustomProvider theme='dark'>
                      <SelectPicker
                        data-testid='units-combobox'
                        onChange={onChange}
                        ref={ref}
                        onBlur={onBlur}
                        searchable={false}
                        value={value}
                        data={measurementOptions}
                        aria-invalid={!!errors.units}
                      />
                    </CustomProvider>
                    {errors.units && (
                      <span role='alert'>{errors.units.message}</span>
                    )}
                  </StyledSelectDiv>
                )
              }}></Controller>

            <Controller
              name='inspection_type'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => {
                return (
                  <StyledSelectDiv>
                    <label>Inspection Type</label>
                    <CustomProvider theme='dark'>
                      <SelectPicker
                        data-testid='inspection-type-combobox'
                        onChange={onChange}
                        ref={ref}
                        onBlur={onBlur}
                        searchable={false}
                        value={value}
                        data={inspectionTypeOptions}
                        aria-invalid={!!errors.inspection_type}
                      />
                    </CustomProvider>
                  </StyledSelectDiv>
                )
              }}></Controller>

            <Controller
              name='vertical_runs'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => {
                return (
                  <StyledSelectDiv>
                    <label>Vertical Runs</label>
                    <CustomProvider theme='dark'>
                      <SelectPicker
                        data-testid='vertical-runs-combobox'
                        onChange={onChange}
                        ref={ref}
                        onBlur={onBlur}
                        searchable={false}
                        value={value}
                        data={[
                          { label: 'True', value: true },
                          { label: 'False', value: false },
                        ]}
                        aria-invalid={!!errors.vertical_runs}
                      />
                    </CustomProvider>
                    {errors.vertical_runs && (
                      <span role='alert'>{errors.vertical_runs.message}</span>
                    )}
                  </StyledSelectDiv>
                )
              }}></Controller>

            <Controller
              name='angle_plot_settings'
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => {
                return (
                  <>
                    <StyledSelectDiv>
                      <label>Angle Plot Settings</label>
                      <CustomProvider theme='dark'>
                        <CheckPicker
                          data-testid='angle-plot-settings-combobox'
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          searchable
                          value={value}
                          data={apsOptions}
                          defaultValue={[]}
                        />
                      </CustomProvider>
                      {errors.angle_plot_settings && (
                        <span role='alert'>
                          {errors.angle_plot_settings.message}
                        </span>
                      )}
                    </StyledSelectDiv>
                  </>
                )
              }}
            />

            <Controller
              name='validation_thickness'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => {
                return (
                  <>
                    <StyledSelectDiv>
                      <label>Validation Thickness</label>
                      <CustomProvider theme='dark'>
                        <SelectPicker
                          data-testid='validation-thickness-combobox'
                          onChange={onChange}
                          searchable={false}
                          value={value}
                          data={validationThicknessOptons}
                          ref={ref}
                          onBlur={onBlur}
                        />
                      </CustomProvider>
                      {errors.validation_thickness && (
                        <span role='alert'>
                          {errors.validation_thickness.message}
                        </span>
                      )}
                    </StyledSelectDiv>
                  </>
                )
              }}></Controller>

            <Controller
              name='ndt_method_type'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => {
                return (
                  <StyledSelectDiv>
                    <label>NDT Method Type</label>
                    <CustomProvider theme='dark'>
                      <SelectPicker
                        data-testid='ndt-method-type-combobox'
                        onChange={onChange}
                        ref={ref}
                        onBlur={onBlur}
                        searchable={false}
                        value={value}
                        data={ndtMethodOptions}
                      />
                    </CustomProvider>
                    {errors.ndt_method_type && (
                      <span role='alert'>{errors.ndt_method_type.message}</span>
                    )}
                  </StyledSelectDiv>
                )
              }}></Controller>

            <Controller
              control={control}
              name='num_points'
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <label>Num Points</label>
                  <StyledFormInput
                    data-testid='num-points-input'
                    type='number'
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder='Num Points'
                    value={value}
                    ref={ref}
                  />
                  {errors.num_points && (
                    <span role='alert'>{errors.num_points.message}</span>
                  )}
                </>
              )}
            />

            <Controller
              control={control}
              name='x_bin_size'
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <label>X Bin Size</label>
                  {xBinSizeUnits ? (
                    <InputAndUnits>
                      <StyledFormInputWithUnits
                        data-testid='x-bin-size-input'
                        type='number'
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder='X Bin Size'
                        value={value}
                        ref={ref}
                      />
                      <FieldUnits>{xBinSizeUnits}</FieldUnits>
                    </InputAndUnits>
                  ) : (
                    <StyledFormInput
                      data-testid='x-bin-size-input'
                      type='number'
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder='X Bin Size'
                      value={value}
                      ref={ref}
                    />
                  )}
                  {errors.x_bin_size && (
                    <span role='alert'>{errors.x_bin_size.message}</span>
                  )}
                </>
              )}
            />

            <Controller
              control={control}
              name='y_bin_size'
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <label>Y Bin Size</label>
                  <InputAndUnits>
                    <StyledFormInputWithUnits
                      data-testid='y-bin-size-input'
                      type='number'
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder='Y Bin Size'
                      value={value}
                      ref={ref}
                    />
                    <FieldUnits>ft</FieldUnits>
                  </InputAndUnits>
                  {errors.y_bin_size && (
                    <span role='alert'>{errors.y_bin_size.message}</span>
                  )}
                </>
              )}
            />

            <Controller
              name='angle_variable'
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => {
                return (
                  <>
                    <StyledSelectDiv>
                      <label>Angle Variable</label>
                      <CustomProvider theme='dark'>
                        <SelectPicker
                          data-testid='angle-variable-combobox'
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          searchable={false}
                          value={value}
                          data={angleVariableOptions}
                        />
                      </CustomProvider>
                      {errors.angle_variable && (
                        <span role='alert'>
                          {errors.angle_variable.message}
                        </span>
                      )}
                    </StyledSelectDiv>
                  </>
                )
              }}
            />

            <Controller
              name='robot_types'
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => {
                return (
                  <>
                    <StyledSelectDiv>
                      <label>Robot Types</label>
                      <CustomProvider theme='dark'>
                        <CheckPicker
                          data-testid='robot-types-combobox'
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          searchable={false}
                          value={value}
                          data={robotTypeOptions}
                          defaultValue={[]}
                        />
                      </CustomProvider>
                      {errors.robot_types && (
                        <span role='alert'>{errors.robot_types.message}</span>
                      )}
                    </StyledSelectDiv>
                  </>
                )
              }}
            />

            <Controller
              control={control}
              name='plot_rotation'
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <label>Plot Rotation</label>
                  <InputAndUnits>
                    <StyledFormInputWithUnits
                      data-testid='plot-rotation-input'
                      type='number'
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder='Plot Rotation'
                      value={value}
                      ref={ref}
                    />
                    <FieldUnits>°</FieldUnits>
                  </InputAndUnits>
                  {errors.plot_rotation && (
                    <span role='alert'>{errors.plot_rotation.message}</span>
                  )}
                </>
              )}
            />

            <Controller
              control={control}
              name='sensor_offset'
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <label>Sensor Offset</label>
                  <StyledFormInput
                    data-testid='sensor-offset-input'
                    type='number'
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder='Sensor Offset'
                    value={value}
                    ref={ref}
                  />
                  {errors.sensor_offset && (
                    <span role='alert'>{errors.sensor_offset.message}</span>
                  )}
                </>
              )}
            />

            <Controller
              control={control}
              name='sensor_spacing'
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <label>Sensor Spacing</label>
                  <InputAndUnits>
                    <StyledFormInputWithUnits
                      data-testid='sensor-spacing-input'
                      type='number'
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder='Sensor Spacing'
                      value={value}
                      ref={ref}
                    />
                    <FieldUnits>in</FieldUnits>
                  </InputAndUnits>
                  {errors.sensor_spacing && (
                    <span role='alert'>{errors.sensor_spacing.message}</span>
                  )}
                </>
              )}
            />

            <Controller
              control={control}
              name='low_threshold_relative_to_nominal'
              render={({ field: { onChange, value, ref } }) => (
                <>
                  <label>Low Threshold Relative to Nominal</label>
                  <StyledFormInput
                    data-testid='low-threshold-input'
                    type='number'
                    onChange={onChange}
                    placeholder='Low Threshold'
                    value={value}
                    ref={ref}
                  />
                  {errors.low_threshold_relative_to_nominal && (
                    <span role='alert'>
                      {errors.low_threshold_relative_to_nominal.message}
                    </span>
                  )}
                </>
              )}
            />

            <Controller
              control={control}
              name='high_threshold_relative_to_nominal'
              render={({ field: { onChange, value, ref } }) => (
                <>
                  <label>High Threshold Relative to Nominal</label>
                  <StyledFormInput
                    data-testid='high-threshold-input'
                    type='number'
                    onChange={onChange}
                    placeholder='High Threshold'
                    value={value}
                    ref={ref}
                  />
                  {errors.low_threshold_relative_to_nominal &&
                    errors.low_threshold_relative_to_nominal.message}
                </>
              )}
            />

            <StyledFormSubmitButton htmlType={'submit'}>
              {submitButtonText}
            </StyledFormSubmitButton>
          </StyledFormRoot>
        </div>
      </StyledInspectionContainer>
    </FormProvider>
  )
}
